/**
 * Client Diagnostics (Fail-Safe)
 *
 * Captures client-side network information to help diagnose
 * if slow requests are caused by user's network limitations.
 *
 * All functions are wrapped in try-catch to ensure they never break
 * the calling code. Returns undefined/partial data on errors.
 *
 * Features:
 * - Network Information API (Chrome/Edge): effectiveType, downlink, rtt, saveData, type
 * - Tab visibility: detects if request completed while tab was hidden
 * - Concurrent requests: tracks in-flight request count
 *
 * Note: Device info (cpuCores, memoryGB, platform) was removed because:
 * - navigator.deviceMemory is capped at 8GB for privacy (unreliable)
 * - navigator.hardwareConcurrency returns logical cores (misleading)
 * - navigator.platform is deprecated and returns generic values
 */

// ============================================================================
// Types
// ============================================================================

export interface NetworkInfo {
  effectiveType?: string // '4g', '3g', '2g', 'slow-2g'
  downlinkMbps?: number // Estimated bandwidth in Mbps
  rttMs?: number // Round-trip time in ms
  saveData?: boolean // Data-saver mode enabled
  type?: string // 'wifi', 'cellular', 'ethernet', 'none', 'unknown'
}

export interface ClientDiagnostics {
  network?: NetworkInfo
  tabVisible?: boolean // Was tab visible when request completed
  concurrentRequests?: number // Number of in-flight requests
}

// ============================================================================
// In-Flight Request Counter
// ============================================================================

/** Track number of in-flight requests */
let inFlightRequestCount = 0

/** Increment in-flight request counter (called when request starts) */
export const incrementInFlightRequests = (): void => {
  try {
    inFlightRequestCount++
  } catch {
    // Silently ignore
  }
}

/** Decrement in-flight request counter (called when request completes) */
export const decrementInFlightRequests = (): void => {
  try {
    if (inFlightRequestCount > 0) {
      inFlightRequestCount--
    }
  } catch {
    // Silently ignore
  }
}

/** Get current in-flight request count (used by TTI trackers) */
export const getInFlightRequestCount = (): number => {
  try {
    return inFlightRequestCount
  } catch {
    return 0
  }
}

// ============================================================================
// Diagnostic Getters
// ============================================================================

/** Get current network information (Chrome/Edge only, fail-safe) */
export const getNetworkInfo = (): NetworkInfo | undefined => {
  try {
    const nav = navigator as any
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection
    if (!connection) return undefined

    return {
      effectiveType: connection.effectiveType,
      downlinkMbps: connection.downlink,
      rttMs: connection.rtt,
      saveData: connection.saveData,
      type: connection.type,
    }
  } catch {
    return undefined
  }
}

/** Get current tab visibility state (fail-safe) */
export const isTabVisible = (): boolean => {
  try {
    return document.visibilityState === 'visible'
  } catch {
    return true // Assume visible if we can't detect
  }
}

/** Get all client diagnostics (fail-safe, returns partial data on errors) */
export const getClientDiagnostics = (): ClientDiagnostics => {
  const diagnostics: ClientDiagnostics = {}

  try {
    diagnostics.network = getNetworkInfo()
  } catch {
    // Silently ignore
  }

  try {
    diagnostics.tabVisible = isTabVisible()
  } catch {
    // Silently ignore
  }

  try {
    diagnostics.concurrentRequests = inFlightRequestCount
  } catch {
    // Silently ignore
  }

  return diagnostics
}
